/**
 * @fileoverview
 * UI State Store - Manages global UI state using Signals
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, signal, computed } from '@angular/core';
import { UIState, DEFAULT_UI_STATE, Theme, Sidebar, Breadcrumb } from '../models';
import { StorageUtil } from '../../utils';
import { STORAGE_KEYS } from '../../constants';

@Injectable({
  providedIn: 'root'
})
export class UIStateStore {
  // Private writable signals
  private uiStateSignal = signal<UIState>(this.loadStateFromStorage());

  // Public readonly signals
  readonly uiState = this.uiStateSignal.asReadonly();

  // Computed signals for specific parts of UI state
  readonly theme = computed(() => this.uiStateSignal().theme);
  readonly sidebar = computed(() => this.uiStateSignal().sidebar);
  readonly breadcrumbs = computed(() => this.uiStateSignal().breadcrumbs);
  readonly isFullscreen = computed(() => this.uiStateSignal().isFullscreen);
  readonly showMobileMenu = computed(() => this.uiStateSignal().showMobileMenu);
  readonly isSidebarCollapsed = computed(() => this.uiStateSignal().sidebar.collapsed);
  readonly isDarkMode = computed(() => this.uiStateSignal().theme.mode === 'dark');

  /**
   * Set theme
   */
  setTheme(theme: Partial<Theme>): void {
    this.uiStateSignal.update(state => ({
      ...state,
      theme: { ...state.theme, ...theme }
    }));
    this.saveStateToStorage();
  }

  /**
   * Toggle theme mode
   */
  toggleTheme(): void {
    const currentMode = this.uiStateSignal().theme.mode;
    this.setTheme({ mode: currentMode === 'light' ? 'dark' : 'light' });
  }

  /**
   * Update sidebar state
   */
  setSidebar(sidebar: Partial<Sidebar>): void {
    this.uiStateSignal.update(state => ({
      ...state,
      sidebar: { ...state.sidebar, ...sidebar }
    }));
    this.saveStateToStorage();
  }

  /**
   * Toggle sidebar collapsed state
   */
  toggleSidebar(): void {
    const currentCollapsed = this.uiStateSignal().sidebar.collapsed;
    this.setSidebar({ collapsed: !currentCollapsed });
  }

  /**
   * Set active menu
   */
  setActiveMenu(menuId: string | null): void {
    this.setSidebar({ activeMenu: menuId });
  }

  /**
   * Set breadcrumbs
   */
  setBreadcrumbs(breadcrumbs: Breadcrumb[]): void {
    this.uiStateSignal.update(state => ({
      ...state,
      breadcrumbs
    }));
  }

  /**
   * Add breadcrumb
   */
  addBreadcrumb(breadcrumb: Breadcrumb): void {
    this.uiStateSignal.update(state => ({
      ...state,
      breadcrumbs: [...state.breadcrumbs, breadcrumb]
    }));
  }

  /**
   * Clear breadcrumbs
   */
  clearBreadcrumbs(): void {
    this.setBreadcrumbs([]);
  }

  /**
   * Toggle fullscreen
   */
  toggleFullscreen(): void {
    const currentFullscreen = this.uiStateSignal().isFullscreen;
    this.uiStateSignal.update(state => ({
      ...state,
      isFullscreen: !currentFullscreen
    }));

    if (!currentFullscreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }

  /**
   * Toggle mobile menu
   */
  toggleMobileMenu(): void {
    const currentShow = this.uiStateSignal().showMobileMenu;
    this.uiStateSignal.update(state => ({
      ...state,
      showMobileMenu: !currentShow
    }));
  }

  /**
   * Close mobile menu
   */
  closeMobileMenu(): void {
    this.uiStateSignal.update(state => ({
      ...state,
      showMobileMenu: false
    }));
  }

  /**
   * Reset UI state to default
   */
  reset(): void {
    this.uiStateSignal.set(DEFAULT_UI_STATE);
    this.clearStateFromStorage();
  }

  /**
   * Load state from storage
   */
  private loadStateFromStorage(): UIState {
    const savedTheme = StorageUtil.getLocal<Theme>(STORAGE_KEYS.THEME);
    const savedSidebarCollapsed = StorageUtil.getLocal<boolean>(STORAGE_KEYS.SIDEBAR_COLLAPSED);

    return {
      ...DEFAULT_UI_STATE,
      theme: savedTheme || DEFAULT_UI_STATE.theme,
      sidebar: {
        ...DEFAULT_UI_STATE.sidebar,
        collapsed: savedSidebarCollapsed ?? DEFAULT_UI_STATE.sidebar.collapsed
      }
    };
  }

  /**
   * Save state to storage
   */
  private saveStateToStorage(): void {
    const state = this.uiStateSignal();
    StorageUtil.setLocal(STORAGE_KEYS.THEME, state.theme);
    StorageUtil.setLocal(STORAGE_KEYS.SIDEBAR_COLLAPSED, state.sidebar.collapsed);
  }

  /**
   * Clear state from storage
   */
  private clearStateFromStorage(): void {
    StorageUtil.removeLocal(STORAGE_KEYS.THEME);
    StorageUtil.removeLocal(STORAGE_KEYS.SIDEBAR_COLLAPSED);
  }
}

